/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Select } from '../../../../../components/Select/Select'
import { useServiceContext } from '../../../../../root'

// Group type constants
const GroupType = {
  USER_DEFINED: 'USER_DEFINED',
  SYSTEM: 'SYSTEM',
  CLOUDADMIN_ACL: 'CLOUDADMIN_ACL'
}

interface SelectUserGroupProps {
  name: string
  value: any
  onChange: (option: any) => void
  isMulti?: boolean
  placeholder?: string
  isDisabled?: boolean
  onlyUsers?: boolean
  onlyGroups?: boolean
}

interface UserGroupOption {
  value: string
  label: string
  id: number
  systemHelperType?: string
  isDisabled?: boolean
}

const SelectUserGroup: React.FC<SelectUserGroupProps> = ({
  name,
  value: propsValue,
  onChange,
  isMulti = false,
  placeholder,
  isDisabled = false,
  onlyUsers = false,
  onlyGroups = false
}) => {
  const [value, setValue] = useState<any>([])
  const [usersGroups, setUsersGroups] = useState<UserGroupOption[]>([])

  const context = useServiceContext()

  const updateValue = (selection: any) => {
    const newValue = Array.isArray(selection) ? selection.map((s: any) => s.value) : selection?.value || null
    setValue(newValue)
    onChange(newValue)
  }

  useEffect(() => {
    if (usersGroups.length > 0) {
      if (Array.isArray(propsValue)) {
        const initialValues: UserGroupOption[] = []
        propsValue.forEach((userGroupItem) => {
          const foundUserGroup = usersGroups.find((ug) => ug.value === userGroupItem)
          if (foundUserGroup) {
            initialValues.push(foundUserGroup)
          }
        })
        if (initialValues.length) {
          setValue(initialValues.map((v) => v.value))
        }
      } else if (!propsValue) {
        setValue([])
      } else {
        const foundUserGroup = usersGroups.find((ug) => ug.value === propsValue)
        if (foundUserGroup) {
          setValue(foundUserGroup.value)
        }
      }
    }
  }, [propsValue, usersGroups])

  useEffect(() => {
    context.api.users
      .getAssembledUsersGroups()
      .then((data) => {
        const sourceData = onlyUsers ? data.users : onlyGroups ? data.groups : data.merged

        const usersGroupsFiltered = sourceData.map((user: any) => {
          return {
            value: user._uid,
            label: user.chName,
            id: user.id,
            systemHelperType: user.systemHelperType,
            ...(user?.type === GroupType.CLOUDADMIN_ACL ? { isDisabled: true } : {})
          }
        })
        setUsersGroups(usersGroupsFiltered)
      })
      .catch((error) => {
        console.error('Error loading users/groups:', error)
      })
  }, [onlyUsers, onlyGroups])

  return (
    <Select
      placeholder={placeholder}
      name={name}
      value={value}
      options={usersGroups}
      onChange={updateValue}
      showNewOptionAtTop={true}
      searchable
      multi={isMulti}
      isDisabled={isDisabled}
    />
  )
}

export default SelectUserGroup
